/**
 * AI Mockup Hooks
 *
 * React hooks for AI mockup generation.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import {
  generateAIMockup,
  getMockupJobStatus,
  getAIMockupSettings,
  updateAIMockupRefinementPrompt,
  getPatchTemplates,
  uploadPatchTemplate,
  updatePatchTemplate,
  deletePatchTemplate,
  getAIMockupBilling,
  type AIMockupSettings,
  type PatchTemplate,
  type TemplateUpdateData,
  type BillingBalance,
} from '../api/mockups';
import { ApiClientError } from '../api/client';

// ============================================
// Types
// ============================================

export interface AIMockupResult {
  proofFileId: string;
  downloadUrl: string;
}

export interface UseAIMockupReturn {
  generating: boolean;
  progress: string | null;
  error: string | null;
  billingError: 'insufficientBalance' | 'powerPackDisabled' | null;
  result: AIMockupResult | null;
  generate: (
    customerArtworkFileId: string,
    productImageUrl: string,
    templateId: string
  ) => Promise<void>;
  clearResult: () => void;
  clearError: () => void;
}

export interface UseBillingBalanceReturn {
  balance: BillingBalance | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export interface UseAIMockupSettingsReturn {
  settings: AIMockupSettings | null;
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  updateRefinementPrompt: (prompt: string) => Promise<boolean>;
  actionLoading: boolean;
}

export interface UsePatchTemplatesReturn {
  templates: PatchTemplate[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
  addTemplate: (file: File, name: string, prompt?: string) => Promise<boolean>;
  updateTemplate: (templateId: string, updates: TemplateUpdateData) => Promise<boolean>;
  removeTemplate: (templateId: string) => Promise<boolean>;
  actionLoading: boolean;
}

// ============================================
// useAIMockup Hook
// ============================================

/**
 * Hook for generating AI mockups using async job polling
 */
export function useAIMockup(orderId: string): UseAIMockupReturn {
  const [generating, setGenerating] = useState(false);
  const [progress, setProgress] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [billingError, setBillingError] = useState<'insufficientBalance' | 'powerPackDisabled' | null>(null);
  const [result, setResult] = useState<AIMockupResult | null>(null);
  const pollingRef = useRef(false);
  const pollTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Clean up polling on unmount
  useEffect(() => {
    return () => {
      pollingRef.current = false;
      if (pollTimeoutRef.current) {
        clearTimeout(pollTimeoutRef.current);
        pollTimeoutRef.current = null;
      }
    };
  }, []);

  const stopPolling = useCallback(() => {
    pollingRef.current = false;
    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current);
      pollTimeoutRef.current = null;
    }
  }, []);

  const startPolling = useCallback((jobId: string) => {
    stopPolling();
    pollingRef.current = true;

    const poll = async () => {
      if (!pollingRef.current) return;

      try {
        console.log(`[mockup] Polling job ${jobId}...`);
        const status = await getMockupJobStatus(jobId);
        console.log(`[mockup] Job status: ${status.status}, progress: ${status.progress}`);

        if (!pollingRef.current) return;

        // Update progress text
        if (status.progress) {
          setProgress(status.progress);
        }

        if (status.status === 'completed' && status.result) {
          stopPolling();
          setGenerating(false);
          setProgress(null);
          setResult({
            proofFileId: status.result.proofFileId,
            downloadUrl: status.result.downloadUrl,
          });
          return;
        } else if (status.status === 'failed') {
          stopPolling();
          setGenerating(false);
          setProgress(null);
          setError(status.error || 'Failed to generate mockup');
          if (status.billingError) {
            setBillingError(status.billingError);
          }
          return;
        }
        // pending/processing — schedule next poll
      } catch (e) {
        console.error('[mockup] Poll error:', e);
      }

      // Schedule next poll if still active
      if (pollingRef.current) {
        pollTimeoutRef.current = setTimeout(poll, 3000);
      }
    };

    // First poll after a short delay
    pollTimeoutRef.current = setTimeout(poll, 2000);
  }, [stopPolling]);

  const generate = useCallback(
    async (customerArtworkFileId: string, productImageUrl: string, templateId: string) => {
      if (!orderId) {
        setError('Order ID is required');
        return;
      }

      if (!templateId) {
        setError('Please select a patch template');
        return;
      }

      setGenerating(true);
      setError(null);
      setBillingError(null);
      setResult(null);
      setProgress('Starting...');

      try {
        const response = await generateAIMockup(
          orderId,
          customerArtworkFileId,
          productImageUrl,
          templateId
        );

        // Start polling with the returned jobId
        startPolling(response.jobId);
      } catch (e) {
        setGenerating(false);
        setProgress(null);
        if (e instanceof ApiClientError && e.data) {
          const data = e.data as unknown as Record<string, unknown>;
          if (data.billingError) {
            setBillingError(data.billingError as 'insufficientBalance' | 'powerPackDisabled');
          }
          setError((data.error as string) || e.message);
        } else {
          const message = e instanceof Error ? e.message : 'Unknown error';
          setError(message);
        }
      }
    },
    [orderId, startPolling]
  );

  const clearResult = useCallback(() => {
    setResult(null);
  }, []);

  const clearError = useCallback(() => {
    setError(null);
    setBillingError(null);
  }, []);

  return {
    generating,
    progress,
    error,
    billingError,
    result,
    generate,
    clearResult,
    clearError,
  };
}

// ============================================
// useAIMockupSettings Hook
// ============================================

/**
 * Hook for managing AI mockup settings (prompt only)
 */
export function useAIMockupSettings(): UseAIMockupSettingsReturn {
  const [settings, setSettings] = useState<AIMockupSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchSettings = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getAIMockupSettings();
      setSettings(data);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load settings';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  const updateRefinementPrompt = useCallback(async (prompt: string): Promise<boolean> => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await updateAIMockupRefinementPrompt(prompt);

      if (response.success) {
        // Update local state
        setSettings((prev) => (prev ? { ...prev, refinementPromptTemplate: prompt } : null));
        return true;
      } else {
        setError(response.error || 'Failed to update refinement prompt');
        return false;
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to update refinement prompt';
      setError(message);
      return false;
    } finally {
      setActionLoading(false);
    }
  }, []);

  return {
    settings,
    loading,
    error,
    refetch: fetchSettings,
    updateRefinementPrompt,
    actionLoading,
  };
}

// ============================================
// usePatchTemplates Hook
// ============================================

/**
 * Hook for managing patch templates
 */
export function usePatchTemplates(): UsePatchTemplatesReturn {
  const [templates, setTemplates] = useState<PatchTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getPatchTemplates();
      setTemplates(data.templates);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load templates';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const addTemplate = useCallback(
    async (file: File, name: string, prompt?: string): Promise<boolean> => {
      setActionLoading(true);
      setError(null);

      try {
        const response = await uploadPatchTemplate(file, name, prompt);

        if (response.success && response.template) {
          // Add to local state
          setTemplates((prev) => [response.template!, ...prev]);
          return true;
        } else {
          setError(response.error || 'Failed to upload template');
          return false;
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to upload template';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  const updateTemplateHandler = useCallback(
    async (templateId: string, updates: TemplateUpdateData): Promise<boolean> => {
      setActionLoading(true);
      setError(null);

      try {
        const response = await updatePatchTemplate(templateId, updates);

        if (response.success && response.template) {
          // Update in local state
          setTemplates((prev) =>
            prev.map((t) => (t.id === templateId ? response.template! : t))
          );
          return true;
        } else {
          setError(response.error || 'Failed to update template');
          return false;
        }
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Failed to update template';
        setError(message);
        return false;
      } finally {
        setActionLoading(false);
      }
    },
    []
  );

  const removeTemplate = useCallback(async (templateId: string): Promise<boolean> => {
    setActionLoading(true);
    setError(null);

    try {
      const response = await deletePatchTemplate(templateId);

      if (response.success) {
        // Remove from local state
        setTemplates((prev) => prev.filter((t) => t.id !== templateId));
        return true;
      } else {
        setError(response.error || 'Failed to delete template');
        return false;
      }
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to delete template';
      setError(message);
      return false;
    } finally {
      setActionLoading(false);
    }
  }, []);

  return {
    templates,
    loading,
    error,
    refetch: fetchTemplates,
    addTemplate,
    updateTemplate: updateTemplateHandler,
    removeTemplate,
    actionLoading,
  };
}

// ============================================
// useBillingBalance Hook
// ============================================

/**
 * Hook for fetching AI image billing balance
 */
export function useBillingBalance(): UseBillingBalanceReturn {
  const [balance, setBalance] = useState<BillingBalance | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchBalance = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const data = await getAIMockupBilling();
      setBalance(data);
    } catch (e) {
      const message = e instanceof Error ? e.message : 'Failed to load billing balance';
      setError(message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch on mount
  useEffect(() => {
    fetchBalance();
  }, [fetchBalance]);

  return {
    balance,
    loading,
    error,
    refetch: fetchBalance,
  };
}
